import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cpu, Activity } from 'lucide-react';
import { PROCESSING_MESSAGES } from '../constants';
import { UserData, GeneratedImages, AllMOSImages } from '../types';
import { generateAllMOSImages } from '../services/replicate';

// Default video for processing
const DEFAULT_PROCESSING_VIDEO = '/Media/I AM AN AMERICAN SOLDIER  BE ALL YOU CAN BE  GOARMY.mp4';

// MOS-specific processing videos
const MOS_PROCESSING_VIDEOS: { [key: string]: string } = {
  'signal': '/Media/SO EARLY  DECIDE TO LEAD  ARMY OFFICER.mp4',
  // Add more MOS-specific videos here
  // 'mechanics': '/Media/mechanics-processing.mp4',
  // 'aviation': '/Media/aviation-processing.mp4',
};

// MOS-specific rank display
const MOS_RANKS: { [key: string]: string } = {
  'mechanics': 'ENLISTED RANK E1: PRIVATE',
  'logistics': 'ENLISTED RANK E1: PRIVATE',
  'signal': 'ENLISTED RANK E1: PRIVATE',
  'aviation': 'ENLISTED RANK E1: PRIVATE',
  'medical': 'ENLISTED RANK E1: PRIVATE',
  'infantry': 'ENLISTED RANK E1: PRIVATE',
};

interface ProcessingScreenProps {
  userData: UserData;
  onComplete: (generatedImages: GeneratedImages) => void;
  onMOSComplete: (mosId: string, images: GeneratedImages) => void;
  onAllMOSComplete: (allImages: AllMOSImages) => void;
  onError: () => void;
}

const ProcessingScreen: React.FC<ProcessingScreenProps> = ({ userData, onComplete, onMOSComplete, onAllMOSComplete, onError }) => {
  const [currentMessage, setCurrentMessage] = useState(0);
  const [progress, setProgress] = useState(0);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImages | null>(null);
  const [isGenerating, setIsGenerating] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasStartedGeneration = useRef(false);
  const priorityCompleteCalledRef = useRef(false);

  // Start video playback
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(console.error);
    }
  }, []);

  // Process messages cycling
  useEffect(() => {
    const messageTimer = setInterval(() => {
      setCurrentMessage(prev => {
        if (prev < PROCESSING_MESSAGES.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 4000);

    return () => clearInterval(messageTimer);
  }, []);

  // Progress bar animation
  useEffect(() => {
    const progressTimer = setInterval(() => {
      setProgress(prev => {
        if (prev < 95 && isGenerating) {
          return prev + 1;
        }
        if (!isGenerating && prev < 100) {
          return 100;
        }
        return prev;
      });
    }, 420);

    return () => clearInterval(progressTimer);
  }, [isGenerating]);

  // Batch-generate all 6 MOS in parallel via Gemini. Priority MOS fires its
  // callback first so the reveal screen appears as soon as it's ready; the
  // remaining MOS continue uploading in the background for instant switching.
  const startGeneration = useCallback(async () => {
    try {
      if (!userData.capturedImage || !userData.sex || !userData.selectedMOS) {
        throw new Error('Missing user data');
      }

      console.log('Starting batch generation for ALL MOS. Priority:', userData.selectedMOS.id);

      const allImages = await generateAllMOSImages(
        userData.capturedImage,
        userData.sex,
        userData.selectedMOS.id,
        userData.lastName,
        // Priority MOS finished — release the reveal screen immediately
        (priorityImages: GeneratedImages) => {
          if (!priorityCompleteCalledRef.current) {
            priorityCompleteCalledRef.current = true;
            console.log('Priority MOS complete, revealing results...');
            setGeneratedImages(priorityImages);
            setIsGenerating(false);
          }
        },
        // Each MOS completion (including priority) — parent uploads to R2 + caches
        onMOSComplete
      );

      console.log('All MOS generation complete');
      onAllMOSComplete(allImages);
    } catch (error) {
      console.error('Error generating images:', error);
      onError();
    }
  }, [userData, onError, onMOSComplete, onAllMOSComplete]);

  // API call - runs once on mount
  useEffect(() => {
    // Prevent duplicate calls
    if (hasStartedGeneration.current) {
      return;
    }
    hasStartedGeneration.current = true;

    startGeneration();
  }, [startGeneration]);

  // Handle video end or generation complete
  const handleVideoEnd = () => {
    if (generatedImages) {
      onComplete(generatedImages);
    }
  };

  // If generation is done and video ended, proceed
  useEffect(() => {
    if (!isGenerating && generatedImages && progress >= 100) {
      // Give a moment for the progress bar to show 100%
      const timer = setTimeout(() => {
        onComplete(generatedImages);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isGenerating, generatedImages, progress, onComplete]);

  return (
    <motion.div
      className="h-full w-full bg-army-black overflow-hidden relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Background Video - MOS specific or default, loops until processing complete */}
      <div className="absolute inset-0">
        <video
          ref={videoRef}
          src={userData.selectedMOS ? (MOS_PROCESSING_VIDEOS[userData.selectedMOS.id] || DEFAULT_PROCESSING_VIDEO) : DEFAULT_PROCESSING_VIDEO}
          className="w-full h-full object-cover"
          playsInline
          loop
          onEnded={handleVideoEnd}
        />
        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {/* HUD Overlay */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Corner brackets */}
        <div className="absolute top-12 left-8 4k:top-24 4k:left-16 w-24 h-24 4k:w-48 4k:h-48 border-t-2 border-l-2 4k:border-t-6 4k:border-l-6 border-army-gold opacity-70" />
        <div className="absolute top-12 right-8 4k:top-24 4k:right-16 w-24 h-24 4k:w-48 4k:h-48 border-t-2 border-r-2 4k:border-t-6 4k:border-r-6 border-army-gold opacity-70" />
        <div className="absolute bottom-32 left-8 4k:bottom-64 4k:left-16 w-24 h-24 4k:w-48 4k:h-48 border-b-2 border-l-2 4k:border-b-6 4k:border-l-6 border-army-gold opacity-70" />
        <div className="absolute bottom-32 right-8 4k:bottom-64 4k:right-16 w-24 h-24 4k:w-48 4k:h-48 border-b-2 border-r-2 4k:border-b-6 4k:border-r-6 border-army-gold opacity-70" />

      </div>

      {/* Top HUD Info */}
      <div className="absolute top-16 left-12 4k:top-32 4k:left-24 z-10">
        <div className="flex items-center space-x-3 4k:space-x-6 mb-2 4k:mb-4">
          <motion.div
            className="w-3 h-3 4k:w-6 4k:h-6 rounded-full bg-red-500"
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1, repeat: Infinity }}
          />
          <span className="text-red-500 font-hud text-sm 4k:text-4xl">PROCESSING</span>
        </div>
        <div className="text-hud-green font-hud text-xs 4k:text-3xl space-y-1 4k:space-y-2">
          <p>SYS: NEURAL_NET_ACTIVE</p>
          <p>STATUS: {isGenerating ? 'GENERATING' : 'COMPLETE'}</p>
        </div>
      </div>

      {/* Top Right HUD */}
      <div className="absolute top-16 right-12 4k:top-32 4k:right-24 z-10 text-right">
        <div className="text-army-gold font-hud text-sm 4k:text-4xl mb-2 4k:mb-4">
          {userData.selectedMOS?.shortTitle || 'MOS'}
        </div>
        <div className="text-hud-green font-hud text-xs 4k:text-3xl space-y-1 4k:space-y-2">
          <p>PROFILE: {userData.sex?.toUpperCase()}</p>
          <p>{userData.selectedMOS ? MOS_RANKS[userData.selectedMOS.id] || 'RANK: OFFICER' : 'RANK: OFFICER'}</p>
        </div>
      </div>

      {/* Center Processing Info */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <div className="text-center">
          {/* Animated Processing Icon */}
          <motion.div
            className="flex justify-center mb-6 4k:mb-12"
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          >
            <div className="relative">
              <div className="w-20 h-20 4k:w-40 4k:h-40 border-4 4k:border-8 border-army-gold/30 rounded-full" />
              <div className="absolute inset-0 w-20 h-20 4k:w-40 4k:h-40 border-4 4k:border-8 border-transparent border-t-army-gold rounded-full" />
              <Cpu className="absolute inset-0 m-auto w-8 h-8 4k:w-16 4k:h-16 text-army-gold" />
            </div>
          </motion.div>

          {/* Title */}
          <motion.h1
            className="text-4xl 4k:text-9xl font-bold text-white mb-2 4k:mb-6 glow-text-subtle"
            animate={{ opacity: [0.8, 1, 0.8] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            GENERATING PROFILE
          </motion.h1>

          {/* Status Message */}
          <AnimatePresence mode="wait">
            <motion.p
              key={currentMessage}
              className="text-lg 4k:text-5xl text-hud-green font-hud"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {PROCESSING_MESSAGES[currentMessage]?.text || ''}
            </motion.p>
          </AnimatePresence>

          {/* Progress Bar - Center */}
          <div className="mt-10 4k:mt-20 w-80 4k:w-[600px] mx-auto">
            <div className="flex justify-between text-sm 4k:text-3xl text-army-gold font-hud mb-2 4k:mb-4">
              <span>PROGRESS</span>
              <span>{progress}%</span>
            </div>
            <div className="h-4 4k:h-8 bg-army-dark/80 border-2 4k:border-4 border-army-gold/60 rounded-sm overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-army-gold via-army-gold to-hud-green"
                style={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <p className="text-center mt-3 4k:mt-6 text-xs 4k:text-2xl text-gray-500 font-hud">
              {isGenerating ? 'AI GENERATION IN PROGRESS' : 'COMPLETE'}
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Progress Section */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        {/* Data Visualization Row */}
        <div className="flex justify-center gap-6 mb-4 px-8">
          {/* Face Mapping */}
          <div className="bg-black/60 border border-army-gold/50 p-3 w-48">
            <div className="flex items-center space-x-2 mb-2">
              <Activity className="w-3 h-3 text-army-gold" />
              <span className="text-xs text-army-gold uppercase font-hud">Face Mapping</span>
            </div>
            <div className="h-12 relative overflow-hidden">
              <svg className="w-full h-full">
                <motion.polyline
                  points="0,24 15,12 30,30 45,18 60,27 75,15 90,24"
                  fill="none"
                  stroke="#d4af37"
                  strokeWidth="2"
                  animate={{ x: [-10, 10, -10] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                />
              </svg>
            </div>
          </div>

          {/* Neural Activity */}
          <div className="bg-black/60 border border-army-gold/50 p-3 w-48">
            <div className="flex items-center space-x-2 mb-2">
              <Cpu className="w-3 h-3 text-army-gold" />
              <span className="text-xs text-army-gold uppercase font-hud">Neural Activity</span>
            </div>
            <div className="grid grid-cols-12 gap-0.5">
              {[...Array(24)].map((_, i) => (
                <motion.div
                  key={i}
                  className="h-3 bg-army-gold"
                  animate={{ opacity: [0.2, 1, 0.2] }}
                  transition={{ duration: 0.5, delay: i * 0.04, repeat: Infinity }}
                />
              ))}
            </div>
          </div>

          {/* Data Stream */}
          <div className="bg-black/60 border border-army-gold/50 p-3 w-48">
            <div className="flex items-center space-x-2 mb-2">
              <Activity className="w-3 h-3 text-army-gold" />
              <span className="text-xs text-army-gold uppercase font-hud">Data Stream</span>
            </div>
            <div className="h-12 overflow-hidden">
              <motion.div
                className="text-[10px] text-hud-green font-hud space-y-0.5"
                animate={{ y: [0, -60] }}
                transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
              >
                <p>0x7F3A: Processing...</p>
                <p>0x8B2C: Mapping...</p>
                <p>0x9D4E: Rendering...</p>
                <p>0xAF60: Optimizing...</p>
                <p>0xC182: Finalizing...</p>
              </motion.div>
            </div>
          </div>
        </div>

      </div>
    </motion.div>
  );
};

export default ProcessingScreen;
